import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LightModeIcon from '@mui/icons-material/LightMode';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { ReactNode, forwardRef } from 'react';
import MaskedInput from 'react-text-mask';
import { CEPRegex, expiredDate, regexBirthDate, regexCardNumber, regexCPF, regexPhone } from '~/components/Input/regex';
import JourneyRideWordmark from '~/assets/icons/JourneyRideWordmark';
import { useThemeMode } from '~/styles/theme';
import CheckoutFlowStepper from './CheckoutFlowStepper';
import JourneyHelpButton from './JourneyHelpButton';
import { useJourneyStickyTop } from './journeyStickyTop';

const CHECKOUT_LAYOUT_MAX_WIDTH = 1234;

export { default as CheckoutFlowStepper } from './CheckoutFlowStepper';
export { default as JourneyLoadingScreen } from './JourneyLoadingScreen';
export { default as JourneyOtpInput, JOURNEY_OTP_LENGTH, type JourneyOtpStatus } from './JourneyOtpInput';

type LayoutProps = {
  title: string;
  description?: string;
  progressPercent?: number;
  children: ReactNode;
  appId: string;
};

type JourneyInputMask = 'phone' | 'cpf' | 'birthdate' | 'zipcode' | 'cardNumber' | 'cardExpiry';

const JOURNEY_INPUT_MASKS: Record<JourneyInputMask, Array<string | RegExp>> = {
  phone: regexPhone,
  cpf: regexCPF,
  birthdate: regexBirthDate,
  zipcode: CEPRegex,
  cardNumber: regexCardNumber,
  cardExpiry: expiredDate
};

type InputProps = {
  appId: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  helperText?: string;
  mask?: JourneyInputMask;
  placeholder?: string;
  /** Estado de erro do Figma: label/borda em `error.main` e fundo `backgroundMute`. */
  error?: boolean;
  /** Mensagem exibida abaixo do campo, com ícone, quando `error` é verdadeiro. */
  errorMessage?: string;
  onBlur?: () => void;
};

type JourneyMaskedInputProps = {
  mask: JourneyInputMask;
  'data-app-id'?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

const JourneyMaskedInput = forwardRef<HTMLElement, JourneyMaskedInputProps>(function JourneyMaskedInput(
  { mask, ...props },
  ref
) {
  return <MaskedInput {...props} mask={JOURNEY_INPUT_MASKS[mask]} placeholderChar={'\u2000'} ref={ref as never} />;
});

type PrimaryButtonProps = {
  appId: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
};

type SecondaryActionProps = {
  appId: string;
  label: string;
  disabled?: boolean;
  onClick: () => void;
};

type SupportHintProps = {
  appId: string;
  message: string;
};

type FieldErrorProps = {
  appId: string;
  message: string;
  id?: string;
};

type CardHeaderProps = {
  appId: string;
  clubName?: string | null;
  sellerName?: string | null;
};

type BrandedScreenProps = {
  appId: string;
  title?: string;
  description?: string;
  progressPercent: number;
  showBack?: boolean;
  onBack?: () => void;
  cardMaxWidth?: number;
  /** Largura da faixa escura do header (voltar logo ajuda); no checkout Figma alargar (~1234). */
  toolbarMaxWidth?: number;
  /** `checkout`: stepper + conteúdo largo sem cartão exterior (personalização pré-pagamento). */
  contentLayout?: 'card' | 'fullscreen' | 'checkout';
  checkoutActiveStep?: 0 | 1 | 2 | 3;
  /**
   * No `checkout`, mostra o stepper «Cotação · Pagamento · Dados do contrato» (padrão). Fluxos fora da compra
   * (ex.: renovação) passam `false` e ficam com o conteúdo largo e a faixa de progresso no header.
   */
  showCheckoutStepper?: boolean;
  /** Faixa roxa abaixo do header; só na cotação inicial (wizard), não na personalização pós-`quote`. */
  showHeaderProgressBar?: boolean;
  /** No layout `card`, centraliza o cartão também na vertical (o wizard de cotação: cartão curto no meio da tela). */
  centerCard?: boolean;
  children: ReactNode;
};

export function StandardJourneyBrandedScreen({
  appId,
  title,
  description,
  progressPercent,
  showBack = true,
  onBack,
  cardMaxWidth = 660,
  toolbarMaxWidth = 660,
  contentLayout = 'card',
  checkoutActiveStep = 0,
  showCheckoutStepper = true,
  showHeaderProgressBar = true,
  centerCard = false,
  children
}: BrandedScreenProps) {
  const withStepper = contentLayout === 'checkout' && showCheckoutStepper;
  const effectiveShowHeaderProgressBar = showHeaderProgressBar && !withStepper;
  const { rootRef, topRef } = useJourneyStickyTop<HTMLDivElement, HTMLDivElement>();

  return (
    <Box
      data-app-id={appId}
      ref={rootRef}
      sx={{ bgcolor: 'background.default', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      {/* Header + barra de progresso + stepper acompanham a rolagem; a altura vai para `--journey-sticky-top`. */}
      <Box
        data-app-id={`${appId}-sticky-top`}
        ref={topRef}
        sx={{ position: 'sticky', top: 0, zIndex: (t) => t.zIndex.appBar }}
      >
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'grey.900',
            display: 'flex',
            justifyContent: 'center',
            minHeight: 56,
            px: { md: '120px', xs: 2 },
            py: 0,
            width: '100%'
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              maxWidth: toolbarMaxWidth,
              width: '100%'
            }}
          >
            {showBack && onBack ? (
              <Button
                aria-label="Voltar"
                color="inherit"
                data-app-id={`${appId}-back`}
                onClick={onBack}
                startIcon={<ArrowBackIosNew sx={{ fontSize: 18 }} />}
                sx={{ color: 'primary.contrastText' }}
              >
                Voltar
              </Button>
            ) : (
              <Box sx={{ width: 88 }} />
            )}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <JourneyRideWordmark />
            </Box>
            <JourneyHelpButton appId={`${appId}-help`} />
          </Box>
        </Box>
        {effectiveShowHeaderProgressBar ? (
          <Box
            data-app-id={`${appId}-header-progress`}
            sx={{ bgcolor: 'background.default', px: 0, py: 0, width: '100%' }}
          >
            <Box sx={{ bgcolor: 'secondary.main', borderRadius: 0, height: 6, width: '100%' }}>
              <Box
                sx={{
                  background: 'linear-gradient(180deg, #e2c7ff 0%, #9537fc 48%, #33006a 100%)',
                  borderRadius: 99,
                  height: '100%',
                  maxWidth: '100%',
                  transition: 'width 0.3s ease-out',
                  width: `${progressPercent}%`
                }}
              />
            </Box>
          </Box>
        ) : null}
        {withStepper ? (
          <Box sx={{ bgcolor: 'background.default' }}>
            <CheckoutFlowStepper activeIndex={checkoutActiveStep} />
          </Box>
        ) : null}
      </Box>
      {contentLayout === 'checkout' ? (
        <Box sx={{ pb: { md: 6, xs: 4 }, pt: { md: 3, xs: 2 }, px: 2, width: '100%' }}>
          <Box sx={{ maxWidth: CHECKOUT_LAYOUT_MAX_WIDTH, mx: 'auto', width: '100%' }}>{children}</Box>
        </Box>
      ) : contentLayout === 'fullscreen' ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            minHeight: 0,
            px: 2,
            py: 6,
            width: '100%'
          }}
        >
          {children}
        </Box>
      ) : (
        <Box
          data-app-id={`${appId}-card-area`}
          sx={{
            alignItems: centerCard ? 'center' : 'flex-start',
            display: 'flex',
            flex: centerCard ? 1 : undefined,
            justifyContent: 'center',
            px: 2,
            py: 4
          }}
        >
          <Card
            elevation={4}
            sx={{
              borderRadius: '24px',
              maxWidth: cardMaxWidth,
              p: 3,
              width: '100%'
            }}
          >
            {title ? (
              <Typography align="center" sx={{ mb: description ? 0.5 : 2 }} variant="subtitle1">
                {title}
              </Typography>
            ) : null}
            {description ? (
              <Typography align="center" sx={{ color: 'text.secondary', mb: 2 }} variant="subtitle2">
                {description}
              </Typography>
            ) : null}
            {children}
          </Card>
        </Box>
      )}
    </Box>
  );
}

export function StandardJourneyLayout({ title, description, progressPercent = 26, children, appId }: LayoutProps) {
  return (
    <Box data-app-id={appId} sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <StandardJourneyHeader appId={`${appId}-header`} />
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 5 }}>
        <Card
          sx={{
            borderRadius: '24px',
            maxWidth: 467,
            p: 3,
            width: '100%'
          }}
        >
          <Typography align="center" variant="subtitle1">
            {title}
          </Typography>
          {description && (
            <Typography align="center" sx={{ color: 'text.secondary', mb: 2 }} variant="subtitle1">
              {description}
            </Typography>
          )}
          <LinearProgress
            data-app-id={`${appId}-progress`}
            sx={{
              '& .MuiLinearProgress-bar': { backgroundColor: 'primary.main' },
              backgroundColor: 'secondary.main',
              borderRadius: 99,
              height: 6,
              mb: 2
            }}
            value={progressPercent}
            variant="determinate"
          />
          {children}
        </Card>
      </Box>
    </Box>
  );
}

export function StandardJourneyHeader({ appId }: { appId: string }) {
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';

  return (
    <Box
      data-app-id={appId}
      sx={{
        alignItems: 'center',
        bgcolor: isDark ? 'grey.900' : 'grey.200',
        display: 'flex',
        height: 104,
        justifyContent: 'flex-end',
        px: 2,
        width: '100%'
      }}
    >
      <IconButton
        aria-label={`Ativar modo ${isDark ? 'claro' : 'escuro'}`}
        data-app-id={`${appId}-theme-toggle`}
        onClick={toggleMode}
        sx={{
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary
        }}
      >
        {isDark ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Box>
  );
}

export function StandardJourneyInput({
  appId,
  label,
  value,
  onChange,
  type = 'text',
  helperText,
  mask,
  placeholder,
  error = false,
  errorMessage,
  onBlur
}: InputProps) {
  const errorId = `${appId}-error`;
  const helperId = `${appId}-helper`;
  const showError = error && Boolean(errorMessage);
  const describedBy = showError ? errorId : helperText ? helperId : undefined;

  return (
    <FormControl data-app-id={`${appId}-control`} error={error} fullWidth sx={{ mt: 1 }}>
      <InputLabel
        data-app-id={`${appId}-label`}
        htmlFor={appId}
        shrink
        sx={{ color: error ? 'error.main' : undefined, position: 'static', transform: 'none' }}
      >
        {label}
      </InputLabel>
      <TextField
        data-app-id={`${appId}-input`}
        error={error}
        id={appId}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        size="small"
        type={mask ? 'text' : type}
        value={value}
        // Figma: campo em erro ganha fundo "background/mute" além da borda vermelha.
        sx={(theme) => ({
          '& .MuiOutlinedInput-root': error ? { backgroundColor: theme.customColors.backgroundMute } : {}
        })}
        InputProps={
          mask
            ? {
                inputComponent: JourneyMaskedInput as never,
                inputProps: {
                  mask,
                  'aria-describedby': describedBy,
                  'aria-invalid': error || undefined,
                  'data-app-id': `${appId}-input-element`
                }
              }
            : {
                inputProps: {
                  'aria-describedby': describedBy,
                  'aria-invalid': error || undefined,
                  'data-app-id': `${appId}-input-element`
                }
              }
        }
      />
      {showError ? (
        <StandardJourneyFieldError appId={errorId} id={errorId} message={errorMessage as string} />
      ) : helperText ? (
        <FormHelperText data-app-id={helperId} id={helperId}>
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}

/**
 * Mensagem de erro abaixo de um campo (Figma "Previsão de erro"): ícone de alerta
 * seguido do texto em `error.dark`. Exportado para os campos que não usam
 * `StandardJourneyInput` (ex.: os `Select` de género e ano de fabricação).
 */
export function StandardJourneyFieldError({ appId, id, message }: FieldErrorProps) {
  return (
    <Box data-app-id={appId} id={id} role="alert" sx={{ alignItems: 'flex-start', display: 'flex', gap: 0.5, mt: 0.5 }}>
      <ErrorOutlineIcon aria-hidden sx={{ color: 'error.dark', fontSize: 14, mt: '1px' }} />
      <Typography sx={{ color: 'error.dark' }} variant="caption">
        {message}
      </Typography>
    </Box>
  );
}

/**
 * Faixa de atribuição no topo do cartão (Figma 2026): clube à esquerda e
 * vendedor à direita. Cada lado só é renderizado quando há dado real — links
 * sem `?channel=` continuam a mostrar apenas o clube.
 */
export function StandardJourneyCardHeader({ appId, clubName, sellerName }: CardHeaderProps) {
  if (!clubName && !sellerName) return null;

  return (
    <Box
      data-app-id={appId}
      sx={{
        color: 'text.secondary',
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
        mb: '24px',
        width: '100%'
      }}
    >
      <Typography data-app-id={`${appId}-club`} sx={{ fontWeight: 500 }} variant="caption">
        {clubName ?? ''}
      </Typography>
      {sellerName ? (
        <Typography data-app-id={`${appId}-seller`} sx={{ textAlign: 'right' }} variant="caption">
          Vendedor: {sellerName}
        </Typography>
      ) : null}
    </Box>
  );
}

export function StandardJourneyPrimaryButton({ appId, label, disabled, onClick }: PrimaryButtonProps) {
  return (
    <Button
      data-app-id={appId}
      disabled={disabled}
      fullWidth
      onClick={onClick}
      size="large"
      sx={{ mt: 2 }}
      variant="contained"
    >
      {label}
    </Button>
  );
}

export function StandardJourneySecondaryAction({ appId, label, disabled, onClick }: SecondaryActionProps) {
  return (
    <Button
      data-app-id={appId}
      disabled={disabled}
      fullWidth
      onClick={onClick}
      sx={(theme) => ({ color: theme.customColors.tertiaryMain, mt: 1 })}
      variant="text"
    >
      {label}
    </Button>
  );
}

export function StandardJourneySupportHint({ appId, message }: SupportHintProps) {
  return (
    <Box
      data-app-id={appId}
      sx={{
        alignItems: 'center',
        bgcolor: 'background.default',
        borderRadius: 1,
        display: 'flex',
        justifyContent: 'center',
        px: 1,
        py: 1
      }}
    >
      <Typography align="center" sx={{ color: 'text.secondary' }} variant="caption">
        {message}
      </Typography>
    </Box>
  );
}

export function StandardJourneyHintIcon({ appId, title }: { appId: string; title: string }) {
  return (
    <Tooltip title={title}>
      <HelpOutlineIcon data-app-id={appId} fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  );
}
